import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../config/api'
import { isSessionTokenViable } from '../config/sessionToken'
import { useFeatureFlags } from './FeatureFlagContext'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState(new Set())
  const [roles, setRoles] = useState([])
  const [venueScope, setVenueScope] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { setFlagsFromLogin, clearFlags, refreshFlags } = useFeatureFlags()

  const hydrateFromUserData = (userData) => {
    setUser(userData)
    setPermissions(new Set(Array.isArray(userData.permissions) ? userData.permissions : []))
    setRoles(Array.isArray(userData.roles) ? userData.roles : (userData.role ? [userData.role] : []))
    setVenueScope(userData.venueScope || null)
  }

  useEffect(() => {
    const storedUser = localStorage.getItem('paysafego_user')
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        if (!isSessionTokenViable(userData?.token)) {
          localStorage.removeItem('paysafego_user')
        } else {
          hydrateFromUserData(userData)
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error)
        localStorage.removeItem('paysafego_user')
      }
    }
    setLoading(false)
  }, [])

  // Re-sync feature flags with the server whenever we have a session (fixes stale
  // paysafego_feature_flags in localStorage and wrong tier when host has no tenant slug).
  useEffect(() => {
    if (loading || !user?.token) return
    refreshFlags()
  }, [loading, user?.token, refreshFlags])

  const login = async (credentials) => {
    try {
      // Clear stale session so patched fetch does not send Bearer; avoids 401 from JWT layer on /api/login
      localStorage.removeItem('paysafego_user')
      const res = await fetch(getLocalApiUrl('/api/login'), { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(credentials || {}) })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Login failed')
      const userData = {
        ...j.user,
        token: j.token,
        permissions: j.permissions || [],
        roles: j.roles || (j.user?.role ? [j.user.role] : []),
        venueScope: j.venueScope || null,
      }
      hydrateFromUserData(userData)
      localStorage.setItem('paysafego_user', JSON.stringify(userData))
      // Store feature flags from login response
      if (j.featureFlags != null && typeof j.featureFlags === 'object') {
        setFlagsFromLogin(j.featureFlags)
      }
      await refreshFlags()
      return { success: true, user: userData }
    } catch (e) { return { success:false, error: e.message } }
  }

  const logout = () => {
    const username = user?.username || user?.sub
    if (user?.token) {
      authFetch(getLocalApiUrl('/api/logout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(username ? { username } : {}),
      }).catch(() => {})
    }
    setUser(null)
    setPermissions(new Set())
    setRoles([])
    setVenueScope(null)
    localStorage.removeItem('paysafego_user')
    clearFlags()
    toast.success('Logged out successfully')
    navigate('/login')
  }

  const isAuthenticated = () => !!user

  /** Matches backend authz: full access regardless of perms array (super_admin often has no rows in role_permissions). */
  const hasFullAccessRole = () => {
    if (!user) return false
    if (user.role === 'admin' || user.role === 'super_admin' || user.role === 'org_admin') return true
    if (roles.includes('admin') || roles.includes('super_admin') || roles.includes('org_admin')) return true
    return false
  }

  // Fine-grained permission check: can('profile-safe/search', 'view')
  // Mirrors backend authz.permissionAllowed (including module/*:action segment wildcards).
  const can = (resource, action) => {
    if (hasFullAccessRole()) return true
    if (!permissions || permissions.size === 0) return false
    const key = `${resource}:${action}`
    if (permissions.has(key)) return true
    // Legacy platform key `approvals:*` ↔ tenant matrix `admin/approvals:*`
    if (resource === 'approvals') {
      const mappedAction = action === 'decide' ? 'approve' : action
      if (permissions.has(`admin/approvals:${mappedAction}`) || permissions.has('admin/approvals:manage')) return true
    }
    if (resource === 'admin/approvals' && action === 'view' && permissions.has('approvals:view')) return true
    if (resource === 'admin/approvals' && (action === 'approve' || action === 'manage') && permissions.has('approvals:decide')) return true
    if (resource === 'compliance-safe/incidents') {
      if (action === 'view' && permissions.has('cases:view')) return true
      if (action === 'manage' && (permissions.has('cases:manage') || permissions.has('cases:view'))) return true
    }
    const wildcards = [`${resource}:*`, `*:${action}`, '*:*']
    if (wildcards.some(k => permissions.has(k))) return true
    for (const entry of permissions) {
      const i = String(entry).lastIndexOf(':')
      if (i <= 0) continue
      const gr = entry.slice(0, i)
      const ga = entry.slice(i + 1)
      if (ga !== action && ga !== '*') continue
      if (gr.endsWith('/*')) {
        const prefix = gr.slice(0, -2)
        if (resource === prefix || (prefix && resource.startsWith(`${prefix}/`))) return true
      }
    }
    return false
  }

  // Backwards-compatible role check — checks if user has a specific role
  const hasRole = (roleName) => {
    if (!user) return false
    return roles.includes(roleName) || user.role === roleName
  }

  // Legacy hasPermission — now delegates to can() or role check
  const hasPermission = (requiredRole) => {
    if (!user) return false
    // If it looks like a permission string (contains ':'), use can()
    if (requiredRole && requiredRole.includes(':')) {
      const i = requiredRole.lastIndexOf(':')
      const resource = requiredRole.slice(0, i)
      const action = requiredRole.slice(i + 1)
      return can(resource, action)
    }
    // Otherwise treat as role name — platform roles have all access
    if (user.role === 'admin' || user.role === 'super_admin' || user.role === 'org_admin' ||
        roles.includes('admin') || roles.includes('super_admin') || roles.includes('org_admin')) return true
    return hasRole(requiredRole)
  }

  // Venue scope helpers
  const hasVenueAccess = (venueId) => {
    if (!venueScope) return true // no scope = unrestricted
    if (venueScope === '*' || venueScope === 'all') return true
    if (Array.isArray(venueScope)) return venueScope.includes(venueId)
    return false
  }

  const getAllowedVenues = () => {
    if (!venueScope || venueScope === '*' || venueScope === 'all') return null // null = all venues
    return Array.isArray(venueScope) ? venueScope : []
  }

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    hasPermission,
    hasRole,
    can,
    permissions,
    roles,
    venueScope,
    hasVenueAccess,
    getAllowedVenues,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
